import { Outlet, useLocation } from "@/lib/router-compat";
import { Suspense, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "./AppSidebar";
import CryptoTicker from "./CryptoTicker";
import MobileTabBar from "./MobileTabBar";
import FloatingDailyRecapPlayerBar from "@/components/daily/FloatingDailyRecapPlayerBar";
import FloatingPodcastPlayerBar from "@/components/podcast/FloatingPodcastPlayerBar";
import FloatingArticlePlayerBar from "@/components/article/FloatingArticlePlayerBar";
import { PullToRefresh } from "@/components/ui/PullToRefresh";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDailyRecapPlayer } from "@/context/DailyRecapPlayerContext";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { useArticleAudioPlayer } from "@/context/ArticleAudioPlayerContext";
import { useBootstrap } from "@/hooks/useBootstrap";
import ThemeLogo from "@/components/ui/ThemeLogo";

export const Layout = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  useBootstrap();
  
  // Check if running as PWA (guarded for server rendering)
  const isPWA = typeof window !== "undefined" &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true);
  
  // Route-based player rendering - let each component handle its own visibility
  const isHomePage = location.pathname === '/' || location.pathname === '/daily';
  const isArticlePage = location.pathname.startsWith('/article/');
  const isPodcastPage = location.pathname.startsWith('/podcasts/');
  const isSocialPage = location.pathname.startsWith('/social');
  
  // Player priority by route: Article > Podcast > Daily Recap
  const showArticle = isMobile && isArticlePage;
  const showPodcast = isMobile && isPodcastPage;
  const showDailyRecap = isMobile && isHomePage;
  
  const mainContent = (
    <main className={isMobile 
      ? "flex-1 flex flex-col overflow-y-auto overscroll-y-contain" 
      : "flex-1 flex flex-col overflow-y-auto overscroll-y-contain"
    }>
      {isMobile && !isSocialPage && (
        <div className="flex justify-center py-2 border-b border-border/40">
          <ThemeLogo className="h-7 w-auto ml-0" />
        </div>
      )}
      <Suspense fallback={
        <div className="p-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      }>
        <Outlet />
      </Suspense>
    </main>
  );
  
  return (
    <SidebarProvider>
      <CryptoTicker />
      <div className={`flex h-dvh w-full relative overflow-hidden pt-8 ${isSocialPage ? "bg-black" : ""}`}>
        <AppSidebar />
        {(isMobile || isPWA) ? (
          <PullToRefresh>{mainContent}</PullToRefresh>
        ) : (
          mainContent
        )}
        {isMobile && <MobileTabBar />}
        {showArticle && (
          <div className="fixed bottom-16 left-0 right-0 z-40">
            <FloatingArticlePlayerBar />
          </div>
        )}
        {showPodcast && (
          <div className="fixed bottom-16 left-0 right-0 z-40">
            <FloatingPodcastPlayerBar />
          </div>
        )}
        {showDailyRecap && (
          <div className="fixed bottom-16 left-0 right-0 z-40">
            <FloatingDailyRecapPlayerBar />
          </div>
        )}
      </div>
    </SidebarProvider>
  );
};
